import React, { ReactElement } from 'react'
import { FormattedRelativeTime } from 'react-intl'
import { selectRelativeUnits } from '../react-intl-helpers'
import { Text } from '@contrast-security-inc/design-system-typography'
import { theme } from '@contrast-security-inc/design-system-foundations'
import { cx, css } from '@emotion/css'

const containerBaseStyles = css`
  padding: 0 16px 0 16px;
  display: flex;
  border-bottom: 1px solid ${theme.color.neutral30};
`

const containerUnreadStyles = css`
  background-color: ${theme.color.white};
`

const containerReadStyles = css`
  background-color: ${theme.color.neutral20};
`

const iconContainerStyles = css`
  padding: 12px 16px 0 0;
`

const contentContainerStyles = css`
  padding-top: 12px;
`

const metadataContainerStyles = css`
  padding-top: 7.5px;
  padding-bottom: 11.5px;
`

export type NotificationRowLayoutGetRef =
  | ((element: HTMLDivElement) => void)
  | undefined

interface NotificationRowLayoutProps {
  className?: string
  testId?: string
  index: number
  style: React.CSSProperties
  read?: boolean
  timestamp?: number
  getRef: NotificationRowLayoutGetRef
  onClick?: () => void
  Icon?: ReactElement
  Content: ReactElement
  Metadata?: ReactElement
}

export function NotificationRowLayout({
  className,
  testId,
  index,
  style,
  read = false,
  timestamp,
  getRef,
  onClick,
  Icon,
  Content,
  Metadata
}: NotificationRowLayoutProps) {
  return (
    <div
      ref={getRef}
      data-testid={testId}
      style={style}
      onClick={() => {
        if (onClick) {
          onClick()
        }
      }}
      className={cx(
        `notification-row-index-${index}`,
        className,
        containerBaseStyles,
        { [containerReadStyles]: read },
        { [containerUnreadStyles]: !read }
      )}
    >
      {Icon && <div className={iconContainerStyles}>{Icon}</div>}
      <div data-testid="notifications-row">
        <div className={contentContainerStyles}>{Content}</div>
        <div
          className={metadataContainerStyles}
          data-testid={`notification-metadata-${index}`}
        >
          {timestamp !== undefined && (
            <NotificationTimestamp index={index} timestamp={timestamp} />
          )}
          {!!Metadata && Metadata}
        </div>
      </div>
    </div>
  )
}

const NotificationTimestamp = ({
  index,
  timestamp
}: {
  index: number
  timestamp: number
}) => {
  const { value, unit } = selectRelativeUnits(new Date(timestamp))
  return (
    <FormattedRelativeTime value={value} unit={unit} numeric="auto">
      {(formattedTime) => (
        <Text
          font="metadata"
          color="neutral70"
          testId={`notification-timestamp-${index}`}
        >
          {formattedTime}
        </Text>
      )}
    </FormattedRelativeTime>
  )
}
